import { supabase } from "../lib/supabaseClient";

/**
 * OTP-only authentication helpers (no passwords anywhere).
 *
 * Sign up:  username + email -> email OTP -> profile username saved
 * Log in:   email -> email OTP
 */

export const TEST_ACCOUNT_EMAILS = [
  "rigrestruser@rigrestr.com",
  "admin@rigrestr.com",
];
export const TEST_ACCOUNT_OTP = "000000";

export const isTestAccountEmail = (email) =>
  TEST_ACCOUNT_EMAILS.includes((email || "").trim().toLowerCase());

export const USERNAME_PENDING_PREFIX = "pending_";

export const isPlaceholderUsername = (username, email) => {
  if (!username || !username.trim()) return true;

  const normalized = username.trim().toLowerCase();
  if (normalized.startsWith(USERNAME_PENDING_PREFIX)) return true;

  const emailLocalPart = email?.split("@")[0]?.trim().toLowerCase();
  if (emailLocalPart && normalized === emailLocalPart) return true;

  return false;
};

export const isValidUsername = (username) =>
  /^[a-zA-Z0-9_]{3,20}$/.test(username);

const mapOtpError = (message, shouldCreateUser) => {
  const lower = (message || "").toLowerCase();
  if (
    lower.includes("signups not allowed") ||
    lower.includes("otp_disabled") ||
    lower.includes("disabled")
  ) {
    return shouldCreateUser
      ? "Email sign-up via code is currently unavailable. Please try again later."
      : "No account found for this email. Please sign up first.";
  }
  if (
    lower.includes("rate limit") ||
    lower.includes("too many") ||
    lower.includes("security purposes")
  ) {
    return "Too many requests. Please wait a few minutes before trying again.";
  }
  if (lower.includes("invalid email") || lower.includes("unable to validate")) {
    return "Please enter a valid email address.";
  }
  return message || "Something went wrong. Please try again.";
};

/**
 * Sends a 6-digit email OTP.
 * @param {string} email
 * @param {{ shouldCreateUser: boolean }} options
 */
export const sendEmailOtp = async (email, { shouldCreateUser }) => {
  if (isTestAccountEmail(email)) {
    return;
  }

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser },
  });

  if (error) throw new Error(mapOtpError(error.message, shouldCreateUser));
};

/**
 * Verifies the 6-digit email OTP. Supabase uses type "email" for
 * signInWithOtp codes and "signup" for auth.signUp confirmation emails, so
 * both are attempted to make sure a correct code is never rejected.
 */
export const verifyEmailOtp = async (email, token) => {
  if (isTestAccountEmail(email) && (token || "").trim() === TEST_ACCOUNT_OTP) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: TEST_ACCOUNT_OTP,
    });

    if (error) {
      throw new Error(error.message || "Test account sign-in failed.");
    }

    return data;
  }

  let lastError = null;

  for (const type of ["email", "signup"]) {
    const response = await supabase.auth.verifyOtp({ email, token, type });

    if (!response.error) return response.data;

    lastError = response.error;
    const message = (response.error.message || "").toLowerCase();
    const typeMismatch =
      message.includes("invalid") ||
      message.includes("expired") ||
      message.includes("not found");
    if (!typeMismatch) break;
  }

  const lower = (lastError?.message || "").toLowerCase();
  if (lower.includes("rate limit") || lower.includes("too many")) {
    throw new Error("Too many attempts. Please wait a moment and try again.");
  }
  throw new Error(
    'The code is incorrect or has expired. Tap "Resend Code" to get a new one.',
  );
};

export const getProfileById = async (id) => {
  const { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", id)
    .single();

  if (error || !profile) throw new Error("Profile not found");
  return profile;
};

export const checkEmailExists = async (email) => {
  const { data, error } = await supabase.rpc("check_email_exists", {
    p_email: email,
  });
  if (error) return false;
  return data === true;
};

export const isUsernameTaken = async (username, excludeUserId) => {
  let query = supabase
    .from("profiles")
    .select("id")
    .eq("username", username.trim().toLowerCase());

  if (excludeUserId) query = query.neq("id", excludeUserId);

  const { data, error } = await query.maybeSingle();
  if (error) return false;
  return Boolean(data);
};

/**
 * Saves the chosen username to the logged-in profile. Creates the profile
 * row if it does not exist yet.
 */
export const saveUsername = async (session, username) => {
  const trimmed = username.trim().toLowerCase();
  const userId = session?.user?.id;
  if (!userId) throw new Error("Session expired. Please sign in again.");

  const { data: existingProfile } = await supabase
    .from("profiles")
    .select("id")
    .eq("id", userId)
    .maybeSingle();

  if (existingProfile) {
    const { error } = await supabase
      .from("profiles")
      .update({ username: trimmed })
      .eq("id", userId);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from("profiles").insert({
      id: userId,
      email: session?.user?.email,
      username: trimmed,
      role: "user",
    });
    if (error) throw new Error(error.message);
  }

  return getProfileById(userId);
};

/**
 * Builds the shape stored in localStorage / UserContext.
 */
export const buildUserData = (session, profile) => ({
  token: session?.access_token,
  user: {
    id: profile.id,
    username: profile.username,
    email: profile.email,
    role: profile.role,
    isAdmin: profile.role === "admin",
    isManager: profile.role === "manager" || profile.role === "uploader",
  },
});

export const isAuthenticated = () => {
  const userData = localStorage.getItem("userData");
  return userData !== null;
};

export const getUserData = () => {
  const userData = localStorage.getItem("userData");
  return userData ? JSON.parse(userData) : null;
};

export const saveUserData = (userData) => {
  localStorage.setItem("userData", JSON.stringify(userData));
};

export const removeUserData = () => {
  localStorage.removeItem("userData");
};
